import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, ChevronRight } from 'lucide-react';
import { LiveScreen, StatusPill, formatNumber } from '@/components/live';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { can, isAdmin, scopeOf } from '@/auth/session';
import type { ApprovalRow, FarmerRow, PlotRow, StockRow } from '@/types/records';

/**
 * What is true right now, for the geography this person actually works in.
 *
 * Every number comes from the same views the screens use, so it is already narrowed by tenant,
 * geography and record scope - a State Admin's dashboard counts their state and nobody tells
 * it to. Nothing is computed from a separate "reporting" query that could disagree with the
 * list it links to.
 *
 * The sections a role cannot open are not shown. An approvals panel reading "0" to somebody
 * who may not approve is not information, it is furniture.
 */
export default function DashboardScreen() {
  const { profile } = useAuth();

  const farmers = useRows<FarmerRow>('farmers', { limit: 500 });
  const plots = useRows<PlotRow>('farm_plots', { limit: 500 });
  const stock = useRows<StockRow>('plot_stock_summary', { limit: 500 }, can(profile, 'plot.inventory', 'read'));
  // Records still in draft: entered but never submitted, so nobody else knows they exist yet.
  const mayInventory = can(profile, 'plot.inventory', 'read');
  const mayHarvest = can(profile, 'plot.harvest', 'read');
  const draftTrees = useRows<{ id: string }>('plot_species_inventories', { filters: { status: 'draft' }, limit: 500 }, mayInventory);
  const draftHarvest = useRows<{ id: string }>('plot_harvest_entries', { filters: { status: 'draft' }, limit: 500 }, mayHarvest);
  const approvals = useRows<ApprovalRow>('approval_inbox', { limit: 200 }, can(profile, 'approval.inbox', 'read'));
  // Renewals (0087): only what is expired or due within 30 days, which is what someone has to act on.
  const renewals = useRows<{ kind: string; due_state: string }>('renewals_due', { orderBy: { column: 'due_on', ascending: true }, limit: 2000 },
    can(profile, 'party.register', 'read'));
  const renewalsExpired = (renewals.data ?? []).filter((row) => row.due_state === 'expired').length;
  const renewalsDue = (renewals.data ?? []).filter((row) => row.due_state === 'due_30').length;

  const figures = useMemo(() => {
    const f = farmers.data ?? [];
    const p = plots.data ?? [];
    const s = stock.data ?? [];
    return {
      farmers: f.length,
      farmersActive: f.filter((row) => row.status === 'verified').length,
      kycOutstanding: f.filter((row) => row.kyc_status !== 'verified').length,
      consentOutstanding: f.filter((row) => row.consent_status !== 'verified').length,
      plots: p.length,
      unmapped: p.filter((row) => !row.is_mapped).length,
      declaredArea: p.reduce((sum, row) => sum + Number(row.declared_area_ha ?? 0), 0),
      unmappedArea: p.filter((row) => !row.is_mapped).reduce((sum, row) => sum + Number(row.declared_area_ha ?? 0), 0),
      farmersUnmapped: f.filter((row) => (row.mapped_plot_count ?? 0) === 0).length,
      farmersPending: f.filter((row) => row.status === 'pending').length,
      plotsPending: p.filter((row) => row.plot_status === 'pending').length,
      outOfStanding: f.filter((row) => row.status === 'suspended' || row.status === 'terminated').length
        + p.filter((row) => row.plot_status === 'suspended' || row.plot_status === 'terminated').length,
      mappedArea: p.reduce((sum, row) => sum + Number(row.mapped_area_ha ?? 0), 0),
      variance: p.filter((row) => Math.abs(Number(row.area_variance_pct ?? 0)) > 10).length,
      standing: s.reduce((sum, row) => sum + (row.net_standing_trees ?? 0), 0),
      pending: (approvals.data ?? []).filter((row) =>
        ['submitted', 'under_review', 'returned'].includes(row.request_status)).length,
      overdue: (approvals.data ?? []).filter((row) => row.is_overdue).length,
    };
  }, [farmers.data, plots.data, stock.data, approvals.data]);

  const where = profile?.geography_scopes.length
    ? profile.geography_scopes
        .map((scope) => `${scope.name}${scope.mode === 'include_children' ? ' and below' : ''}`)
        .join(', ')
    : isAdmin(profile) ? 'the whole organisation' : 'no geography assigned yet';

  const scope = scopeOf(profile, 'farmer.register');
  const mayApprove = can(profile, 'approval.inbox', 'approve');
  const farmsTitle = isAdmin(profile) ? 'All farms'
    : scope === 'own' ? 'Your farms'
    : scope === 'team' ? "Your team's farms"
    : 'Farms in your area';

  return (
    <LiveScreen
      subtitle={`Figures for ${where}.`}
      title="Dashboard"
    >
      {profile && profile.geography_scopes.length === 0 && !isAdmin(profile) && (
        <p className="login-alert" role="alert">
          <AlertTriangle size={14} /> No place has been assigned to you yet, so these figures are
          empty. Ask your administrator to assign your district or block.
        </p>
      )}

      {/* Hero cards (owner, 2026-09-21, after a sample): what this person is responsible for,
          then what is still open. Titles follow their reach, and a card whose action the role
          cannot take is replaced by the one it can. */}
      <div className="dash-hero">
        <DashCard title={farmsTitle} to="/farmers">
          <DashFigure label="Farmers" value={formatNumber(figures.farmers)} />
          <DashFigure label="Plots" value={formatNumber(figures.plots)} />
          <DashFigure label="Mapped area" value={`${formatNumber(figures.mappedArea, 2)} ha`} />
          {can(profile, 'plot.inventory', 'read') && (
            <DashFigure label="Standing trees" value={formatNumber(figures.standing)} />
          )}
        </DashCard>

        <DashCard title="Pending geofencing" to="/plots">
          <DashFigure label="Plots" pending={figures.unmapped} total={formatNumber(figures.plots)} />
          <DashFigure label="Declared area (ha)" pending={Number(figures.unmappedArea.toFixed(2))}
                      total={formatNumber(figures.declaredArea, 2)} />
          <DashFigure label="Farmers" pending={figures.farmersUnmapped} total={formatNumber(figures.farmers)} />
        </DashCard>

        {mayApprove ? (
          <DashCard title="Waiting on your decision" to="/inbox">
            <DashFigure big label={figures.overdue > 0 ? `${formatNumber(figures.overdue)} past due` : 'requests'}
                        pending={figures.pending} />
          </DashCard>
        ) : (
          <DashCard title="Awaiting verification" to="/farmers">
            <DashFigure label="Farmers" pending={figures.farmersPending} />
            <DashFigure label="Plots" pending={figures.plotsPending} />
          </DashCard>
        )}

        <div className="dash-hero-pair">
          <DashCard title="KYC & consent" to="/kyc">
            <DashFigure label="KYC pending" pending={figures.kycOutstanding} />
            <DashFigure label="Consent pending" pending={figures.consentOutstanding} />
          </DashCard>
          {(
            <DashCard title="Drafts not finished" to="/farmers">
              <DashFigure label="Farmers" pending={(farmers.data ?? []).filter((row) => row.is_draft).length} />
              {mayInventory && <DashFigure label="Tree counts" pending={draftTrees.data?.length ?? 0} />}
              {mayHarvest && <DashFigure label="Harvests" pending={draftHarvest.data?.length ?? 0} />}
            </DashCard>
          )}
          {can(profile, 'party.register', 'read') && (
            <DashCard title="Renewals due" to="/renewals">
              <DashFigure label="Expired" pending={renewalsExpired} />
              <DashFigure label="Due in 30 days" pending={renewalsDue} />
            </DashCard>
          )}
          <DashCard title="Needs a look" to="/plots">
            <DashFigure label="Area off > 10%" pending={figures.variance} />
            <DashFigure label="Suspended / ended" pending={figures.outOfStanding} />
          </DashCard>
        </div>
      </div>

      <div className="dash-row">
        {can(profile, 'approval.inbox', 'read') && (
          <section className="card">
            <div className="section-heading">
              <h3>Approvals</h3>
              <Link to="/inbox">Open inbox <ArrowRight size={13} /></Link>
            </div>
            {figures.pending === 0 ? (
              <p className="muted" style={{ margin: 0 }}>Nothing is waiting.</p>
            ) : (
              <>
                <ul className="dash-list">
                  <li><span>Waiting on a decision</span><strong>{formatNumber(figures.pending)}</strong></li>
                  {figures.overdue > 0 && (
                    <li>
                      <span style={{ color: 'var(--error)' }}>Past their due date</span>
                      <strong style={{ color: 'var(--error)' }}>{formatNumber(figures.overdue)}</strong>
                    </li>
                  )}
                </ul>
                <ul className="dash-list">
                  {(approvals.data ?? []).slice(0, 4).map((row) => (
                    <li key={row.id}>
                      <span>{row.entity_label ?? row.entity_table}</span>
                      <StatusPill value={row.request_status} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        )}

        <section className="card">
          <div className="section-heading"><h3>Your access</h3></div>
          <ul className="dash-list">
            <li><span>Role</span><strong>{profile?.roles.map((role) => role.name).join(', ') || 'None'}</strong></li>
            <li><span>Places</span><strong>{where}</strong></li>
            <li>
              <span>Records you see</span>
              <strong>{scope === 'all' ? 'All in your places' : scope === 'team' ? "Your team's" : 'Only your own'}</strong>
            </li>
            <li><span>Signed in as</span><strong>{profile?.user.full_name}</strong></li>
          </ul>
          <p className="muted" style={{ margin: 0, fontSize: '12.5px' }}>
            Only an administrator can change any of this. If something here is wrong, ask them
            rather than working around it.
          </p>
        </section>
      </div>
    </LiveScreen>
  );
}

/** A dashboard hero card: a title, a chevron, and the whole card links to the list behind it. */
function DashCard({ title, to, children }: { title: string; to: string; children: ReactNode }) {
  return (
    <Link className="dash-card" to={to}>
      <span className="dash-card-head">{title}<ChevronRight size={20} /></span>
      <span className="dash-card-figures">{children}</span>
    </Link>
  );
}

/**
 * One figure. With `pending`, the open count is highlighted - over the total when there is one
 * (100/1000) - because the open part is the one somebody has to act on.
 */
function DashFigure({ label, value, pending, total, big }: {
  label: string; value?: string; pending?: number; total?: string; big?: boolean;
}) {
  return (
    <span className={`dash-figure${big ? ' big' : ''}`}>
      <strong>
        {pending !== undefined
          ? <><em className={pending > 0 ? 'open' : 'clear'}>{formatNumber(pending, Number.isInteger(pending) ? 0 : 2)}</em>{total !== undefined && <>/{total}</>}</>
          : value}
      </strong>
      <small>{label}</small>
    </span>
  );
}
